import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import FileUpload from "@mui/icons-material/FileUpload";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { AssetCount } from "../../modules/assets/asset-count";
import { AssetGrid } from "../../modules/assets/asset-grid";
import { AssetPreviewModal } from "../../modules/assets/asset-preview-modal";
import { ContentLibraryFilter } from "../../modules/assets/components/content-library-filter";
import { ContentTypeChips } from "../../modules/assets/content-type-chips";
import { TagChips } from "../../modules/assets/tag-chips";
import { EmptyState } from "../../modules/assets/empty-state";
import { useAssets } from "../../modules/assets/hooks/use-assets";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import { UploadNewFileModal } from "../../modules/assets/upload-new-file-modal";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useMultiformat } from "../../modules/multiformat/hooks/use-multi-format";
import { SearchField } from "../../modules/search/search-field";
import { useFlag } from "../../server/optimizely";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";
import { contentTypes as allContentTypes } from "./content-types";
import { CreateContentCardsPane } from "./create-content-cards-pane";

const ContentLibraryPage: FC = () => {
  const { t } = useTranslation();
  const [video_uploads] = useFlag("video_uploads");
  useMultiformat();
  const { data, loading, error, reload } = useAssets();
  const [filteredData, setFilteredData] = useState<null | Asset[]>(null);
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [appliedTags, setAppliedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const previewModal = useDialog();
  const uploadModal = useDialog();

  const account = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedAsset = useMemo(() => {
    const previewId = searchParams.get("preview");
    if (!previewId || loading || !data) return null;

    return data?.find((x) => x.id === (previewId as UUID)) ?? null;
  }, [searchParams, data, loading]);

  useEffect(() => {
    if (selectedAsset && !previewModal.open) {
      previewModal.handleOpen();
      return;
    }

    if (!selectedAsset && previewModal.open) {
      previewModal.handleClose();
    }
  }, [selectedAsset, previewModal]);

  useEffect(() => {
    const formats = searchParams.has("format") && searchParams.getAll("format");
    const tags = searchParams.has("tags") && searchParams.getAll("tags");

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setContentTypes(formats ? formats : []);
    setAppliedTags(tags ? tags : []);

    if (!data) {
      setFilteredData(null);
      setAvailableTags([]);
      return;
    }

    // Extract all unique tags from assets
    const allTags = Array.from(
      new Set(
        data.filter((asset) => asset.tags && asset.tags.length > 0).flatMap((asset) => asset.tags!),
      ),
    ).sort();
    setAvailableTags(allTags);

    // Apply content type filtering
    let filtered = data;
    if (formats && formats.length > 0) {
      filtered = filtered.filter((x) => formats.includes(x.content_type));
    }

    // Apply tag filtering
    if (tags && tags.length > 0) {
      filtered = filtered.filter((x) => x.tags && x.tags.some((tag) => tags.includes(tag)));
    }

    setFilteredData(filtered);

    // Apply search text filtering if present
    if (!searchText.trim()) return;

    const f = searchText.trim().toLowerCase();
    setFilteredData((previousFilteredData) => {
      if (!previousFilteredData) return null;
      return previousFilteredData.filter(
        (a) =>
          (a.title.toLowerCase().includes(f) || a.content_type.toLowerCase().includes(f)) &&
          (formats && formats.length > 0 ? formats.includes(a.content_type) : true) &&
          (tags && tags.length > 0 ? a.tags && a.tags.some((tag) => tags.includes(tag)) : true),
      );
    });
  }, [data, searchParams, searchText]);

  useEffect(() => {
    const ts = searchParams.has("format") ? searchParams.getAll("format") : allContentTypes;
    const selectedTagsFromUrl = searchParams.has("tags") ? searchParams.getAll("tags") : [];
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedContentTypes(ts);
    setSelectedTags(selectedTagsFromUrl);
  }, [setSelectedContentTypes, setSelectedTags, searchParams]);

  const handleSelectAsset = useCallback(
    (asset: Asset) => {
      const params = new URLSearchParams(searchParams);
      params.set("preview", asset.id);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleClosePreviewModal = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("preview");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleDeleted = useCallback(() => {
    handleClosePreviewModal();
    reload();
  }, [handleClosePreviewModal, reload]);

  const handleRemoveContentTypeFilter = useCallback(
    (contentType: string) => {
      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("format").filter((c) => c !== contentType);
      params.delete("format");
      newValues.forEach((val) => params.append("format", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveTagFilter = useCallback(
    (tag: string) => {
      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("tags").filter((tagValue) => tagValue !== tag);
      params.delete("tags");
      newValues.forEach((val) => params.append("tags", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilter = useCallback(() => {
    const params = new URLSearchParams();
    selectedContentTypes.forEach((c) => params.append("format", c));
    selectedTags.forEach((tag) => params.append("tags", tag));
    setSearchParams(params);
  }, [selectedContentTypes, selectedTags, setSearchParams]);

  if (account.loading) return null;

  return (
    <>
      <Page title={t("Your Content Library")}>
        <PageHeader>
          <Box display="flex" justifyContent="space-between">
            <PageHeading heading={t("Your Content Library")} />
            <Box>
              <Button
                onClick={uploadModal.handleOpen}
                variant="outlined"
                color="primary"
                data-analytics-id="content-library-upload-file"
                startIcon={<FileUpload />}
                sx={{ mr: 2 }}
              >
                {t("Upload file")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                href="/create-content"
                data-analytics-id="content-library-create-content-cta"
                startIcon={<AutoFixHigh />}
              >
                {t("Create Content")}
              </Button>
            </Box>
          </Box>
        </PageHeader>
        {data && data.length !== 0 && <CreateContentCardsPane />}
        <PageContent maxWidth="xl">
          {loading && <CircularProgress />}
          {error && (
            <Alert severity="error">
              {t("Sorry, there was a problem retrieving your content. Please try again later")}
            </Alert>
          )}
          {filteredData && (
            <Stack>
              <Stack direction="row" justifyContent={"space-between"} pb={2}>
                <Box flexGrow={1} mr={2}>
                  <SearchField
                    defaultValue={searchText ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchText(e.target.value);
                    }}
                    fullWidth
                  />
                </Box>
                <ContentLibraryFilter
                  handleApplyFilter={handleApplyFilter}
                  selectedContentTypes={selectedContentTypes}
                  setSelectedContentTypes={setSelectedContentTypes}
                  contentTypes={allContentTypes}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  availableTags={availableTags}
                />
              </Stack>
              <Stack direction="row" justifyContent={"space-between"}>
                <AssetCount assets={filteredData} searchText={searchText} />
              </Stack>
              <Stack gap={1} sx={{ mb: 2 }}>
                <ContentTypeChips
                  onDelete={handleRemoveContentTypeFilter}
                  contentTypes={contentTypes}
                />
                <TagChips onDelete={handleRemoveTagFilter} tags={appliedTags} />
              </Stack>
              <AssetGrid
                assets={filteredData}
                onClick={handleSelectAsset}
                designer={account.data?.designer}
              />
            </Stack>
          )}
          {data && data.length === 0 && <EmptyState />}
        </PageContent>
      </Page>
      <AssetPreviewModal
        asset={selectedAsset}
        open={previewModal.open}
        onClose={handleClosePreviewModal}
        onChange={reload}
        onDeleted={handleDeleted}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
      />
      <UploadNewFileModal
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={uploadModal.handleClose}
        onCancel={uploadModal.handleClose}
        onChange={reload}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        videoUploads={video_uploads}
      />
    </>
  );
};

export default ContentLibraryPage;
